package org.moxultimate.topwithmox.appscrud.main

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.last
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.moxultimate.topwithmox.apps.api.AppsRepository
import org.moxultimate.topwithmox.apps.data.AppsResponse
import org.moxultimate.topwithmox.appscrud.api.AppsCRUDRepository
import org.moxultimate.topwithmox.core.base.ViewModel
import org.moxultimate.topwithmox.core.state.APIState
import org.moxultimate.topwithmox.utils.toTitleCase
import org.w3c.files.File
import org.w3c.files.FileReader
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException

/**
 * AppsCRUDViewModel
 * Manages cursor-based pagination, client-side type/search filtering,
 * and create/update/delete operations for app entries.
 *
 * @param repository Read-only data source for apps, tags, and types.
 * @param crudRepository Write repository for create, update, delete, saveTag, and saveType.
 */
class AppsCRUDViewModel(
    private val repository: AppsRepository,
    private val crudRepository: AppsCRUDRepository,
) : ViewModel() {

    // ── Operation state (create / update / delete) ────────────────────────────

    private val _crudState = MutableStateFlow<AppsCRUDState>(AppsCRUDState.Idle)
    val crudState: StateFlow<AppsCRUDState> = _crudState

    // ── List state ────────────────────────────────────────────────────────────

    private val _listState = MutableStateFlow<AppsListState>(AppsListState.Loading)
    val listState: StateFlow<AppsListState> = _listState

    private val _isLoadingMore = MutableStateFlow(false)
    val isLoadingMore: StateFlow<Boolean> = _isLoadingMore

    private val _hasMoreInCurrentView = MutableStateFlow(true)
    val hasMoreInCurrentView: StateFlow<Boolean> = _hasMoreInCurrentView

    // ── Accumulated apps ──────────────────────────────────────────────────────

    private val accumulatedApps = mutableListOf<AppsResponse>()
    private var lastId: Long = Long.MAX_VALUE
    private var hasMore = true

    // ── Filter state ──────────────────────────────────────────────────────────

    private val _allTypes = MutableStateFlow<List<String>>(emptyList())
    val allTypes: StateFlow<List<String>> = _allTypes

    private val _allTags = MutableStateFlow<List<String>>(emptyList())
    val allTags: StateFlow<List<String>> = _allTags

    private val _selectedTypes = MutableStateFlow<Set<String>>(emptySet())
    val selectedTypes: StateFlow<Set<String>> = _selectedTypes

    private val _searchText = MutableStateFlow("")
    val searchText: StateFlow<String> = _searchText

    // ── List loading ──────────────────────────────────────────────────────────

    /**
     * loadApps
     * Resets accumulated state and fetches the first page. Call from LaunchedEffect(Unit).
     */
    fun loadApps() {
        accumulatedApps.clear()
        lastId = Long.MAX_VALUE
        hasMore = true
        _selectedTypes.value = emptySet()
        _searchText.value = ""
        _hasMoreInCurrentView.value = true
        fetchPage(isInitialLoad = true)
    }

    /**
     * loadMoreIfNeeded
     * Called by the scroll handler when the user nears the bottom.
     * No-op when already loading or no more pages exist.
     */
    fun loadMoreIfNeeded() {
        if (hasMore && !_isLoadingMore.value) fetchPage(isInitialLoad = false)
    }

    private fun fetchPage(isInitialLoad: Boolean) {
        viewModelScope.launch {
            _isLoadingMore.value = true
            repository.getApps(lastId).collect { state ->
                _listState.value = when (state) {
                    is APIState.Loading -> {
                        if (isInitialLoad) AppsListState.Loading else _listState.value
                    }
                    is APIState.Error -> {
                        _isLoadingMore.value = false
                        AppsListState.Error(state.error, state.statusCode)
                    }
                    is APIState.Success -> {
                        val page = state.data
                        _isLoadingMore.value = false
                        if (page.size < 20) hasMore = false
                        accumulatedApps.addAll(page)
                        if (page.isNotEmpty()) lastId = page.last().id
                        _hasMoreInCurrentView.value = hasMore
                        buildListState()
                    }
                }
            }
        }
    }

    // ── Type / Tag loading ────────────────────────────────────────────────────

    /**
     * loadAllTypes
     * Fetches all distinct app types. Call once on page load.
     */
    fun loadAllTypes() {
        viewModelScope.launch {
            repository.getAllTypes().collect { state ->
                if (state is APIState.Success) _allTypes.value = state.data.distinct().sorted()
            }
        }
    }

    /**
     * loadAllTags
     * Fetches all app tags for search autocomplete. Call once on page load.
     */
    fun loadAllTags() {
        viewModelScope.launch {
            repository.getAllTags().collect { state ->
                if (state is APIState.Success) _allTags.value = state.data.distinct().sorted()
            }
        }
    }

    // ── Filtering ─────────────────────────────────────────────────────────────

    /**
     * toggleType
     * Adds or removes [type] from the active type filter and rebuilds state client-side.
     *
     * @param type Type string matching a value from [allTypes].
     */
    fun toggleType(type: String) {
        val current = _selectedTypes.value
        _selectedTypes.value = if (type in current) current - type else current + type
        _listState.value = buildListState()
    }

    /**
     * setSearchText
     * Updates the live search text and immediately re-filters the loaded apps list.
     *
     * @param text Text to search for in app title and description.
     */
    fun setSearchText(text: String) {
        _searchText.value = text
        _listState.value = buildListState()
    }

    /**
     * clearAllFilters
     * Resets all active type chips and search text, restoring the full apps list.
     */
    fun clearAllFilters() {
        _selectedTypes.value = emptySet()
        _searchText.value = ""
        _listState.value = buildListState()
    }

    private fun buildListState(): AppsListState.Success {
        val types = _selectedTypes.value
        val text = _searchText.value.trim()
        val result = accumulatedApps.filter { app ->
            val matchesType = types.isEmpty() ||
                    types.any { type -> app.type?.equals(type.trim(), ignoreCase = true) == true }
            val matchesText = text.isBlank() ||
                    app.title?.contains(text, ignoreCase = true) == true ||
                    app.tags?.contains(text, ignoreCase = true) == true ||
                    app.description?.contains(text, ignoreCase = true) == true
            matchesType && matchesText
        }
        return AppsListState.Success(apps = result, hasMore = hasMore)
    }

    // ── CRUD operations ───────────────────────────────────────────────────────

    /**
     * createApp
     * Reads image and video files from [entry] then creates a new app via POST /apps.
     * Also persists the type to its lookup table.
     *
     * @param entry Form data for the new app (imageFile and videoFile must be non-null).
     */
    fun createApp(entry: AppsFormEntry) {
        viewModelScope.launch {
            _crudState.value = AppsCRUDState.Loading
            try {
                val imageBytes = readFileAsBytes(entry.imageFile!!)
                when (val result = crudRepository.createApp(
                    title       = entry.title,
                    type        = entry.type,
                    tags        = entry.tags,
                    description = entry.description,
                    videoLink   = entry.videoLink,
                    imageBytes  = imageBytes,
                ).last()) {
                    is APIState.Error -> _crudState.value = AppsCRUDState.Error(result.error, result.statusCode)
                    is APIState.Success -> {
                        persistType(entry.type)
                        persistTags(entry.tags)
                        _crudState.value = AppsCRUDState.Success("App created successfully!")
                    }
                    else -> Unit
                }
            } catch (e: Throwable) {
                _crudState.value = AppsCRUDState.Error(e.message ?: "Create failed")
            }
        }
    }

    /**
     * updateApp
     * Reads optional new files from [entry] then updates an existing app via PUT /apps/{id}.
     *
     * @param id App ID to update.
     * @param entry Updated form data (imageFile and videoFile are optional for updates).
     */
    fun updateApp(id: Long, entry: AppsFormEntry) {
        viewModelScope.launch {
            _crudState.value = AppsCRUDState.Loading
            try {
                val imageBytes = entry.imageFile?.let { readFileAsBytes(it) }
                when (val result = crudRepository.updateApp(
                    id          = id,
                    title       = entry.title.takeIf { it.isNotBlank() },
                    type        = entry.type.takeIf { it.isNotBlank() },
                    tags        = entry.tags,
                    description = entry.description.takeIf { it.isNotBlank() },
                    videoLink   = entry.videoLink.takeIf { it.isNotBlank() },
                    imageBytes  = imageBytes,
                ).last()) {
                    is APIState.Error -> _crudState.value = AppsCRUDState.Error(result.error, result.statusCode)
                    is APIState.Success -> {
                        persistType(entry.type)
                        persistTags(entry.tags)
                        _crudState.value = AppsCRUDState.Success("App updated successfully!")
                    }
                    else -> Unit
                }
            } catch (e: Throwable) {
                _crudState.value = AppsCRUDState.Error(e.message ?: "Update failed")
            }
        }
    }

    /**
     * deleteApp
     * Deletes an app entry and its S3 files via DELETE /apps/{id}.
     *
     * @param id App ID to delete.
     */
    fun deleteApp(id: Long) {
        viewModelScope.launch {
            _crudState.value = AppsCRUDState.Loading
            try {
                when (val result = crudRepository.deleteApp(id).last()) {
                    is APIState.Error -> _crudState.value = AppsCRUDState.Error(result.error, result.statusCode)
                    else -> _crudState.value = AppsCRUDState.Success("App deleted successfully!")
                }
            } catch (e: Throwable) {
                _crudState.value = AppsCRUDState.Error(e.message ?: "Delete failed")
            }
        }
    }

    /**
     * resetState
     * Resets the CRUD operation state back to Idle.
     */
    fun resetState() {
        _crudState.value = AppsCRUDState.Idle
    }

    // ── Helpers ───────────────────────────────────────────────────────────────

    private fun persistType(type: String) {
        viewModelScope.launch {
            val titleCased = type.trim().toTitleCase()
            if (titleCased.isNotBlank()) crudRepository.saveType(titleCased).collect {}
        }
    }

    /**
     * persistTags
     * Splits the pipe-separated [tags] string, Title-Cases each entry, and fires a
     * best-effort POST /apps-tag for every unique non-blank tag.
     * Errors are silently discarded — lookup table updates never block the main flow.
     *
     * @param tags Pipe-separated tag string from the form.
     */
    private fun persistTags(tags: String) {
        tags.split("|")
            .map { it.trim().toTitleCase() }
            .filter { it.isNotBlank() }
            .distinct()
            .forEach { tag ->
                viewModelScope.launch {
                    try { crudRepository.saveTag(tag).collect {} } catch (_: Throwable) {}
                }
            }
    }

    /**
     * readFileAsBytes
     * Reads a browser File object as a ByteArray using the FileReader API.
     * Suspends until the read is complete or throws on failure.
     *
     * @param file The browser File to read.
     * @return ByteArray of the file's contents.
     */
    private suspend fun readFileAsBytes(file: File): ByteArray = suspendCancellableCoroutine { cont ->
        val reader = FileReader()
        reader.onload = {
            val buffer = (reader.result as ArrayBuffer)
            val bytes = Int8Array(buffer).unsafeCast<ByteArray>()
            cont.resume(bytes)
        }
        reader.onerror = {
            cont.resumeWithException(Exception("Failed to read file: ${file.name}"))
        }
        reader.readAsArrayBuffer(file)
    }
}

/**
 * AppsFormEntry
 * UI-layer form state for a single app entry (add or edit).
 * [imageFile] and [videoFile] are null when no new file has been selected.
 */
data class AppsFormEntry(
    val imageFile: File? = null,
    val title: String = "",
    val type: String = "",
    val tags: String = "",
    val description: String = "",
    val videoLink: String = "",
) {
    /** True when all required fields and the image file are present (used for add flow). */
    val isValid: Boolean
        get() = imageFile != null && title.isNotBlank() && type.isNotBlank()

    /** True when required text fields are filled (used for update flow — image is optional). */
    val isValidForUpdate: Boolean
        get() = title.isNotBlank() && type.isNotBlank()

    /** Names of required fields that are still empty or missing — used to show validation hints. */
    val missingFields: List<String>
        get() = buildList {
            if (imageFile == null) add("Image")
            if (title.isBlank()) add("Title")
            if (type.isBlank()) add("Type")
        }
}

/**
 * toFormEntry
 * Convenience extension to pre-populate an [AppsFormEntry] from an [AppsResponse].
 */
fun AppsResponse.toFormEntry() = AppsFormEntry(
    title       = title.orEmpty(),
    type        = type.orEmpty(),
    tags        = tags.orEmpty(),
    description = description.orEmpty(),
    videoLink   = videoLink.orEmpty(),
)
